import { browser } from "$app/environment"
import {
  getCookieAuthOverride,
  isCookieAuthEnabled,
  isCookieModeRequested,
  setCookieAuthEnabled,
} from "$lib/cookie-auth"
import { TastyLDClient } from "$lib/launchdarkly"
import { twApiClient } from "$lib/tastyworks-rest"
import session from "$lib/tastyworks-rest/session"
import { captureUtmParams } from "$lib/utm/utm-capture"
import {
  AO_AUTH_SESSION_COOKIE_KEY,
  type TwUser,
} from "@tastyworks/tastyworks-api"
import { CROSS_DOMAIN_COOKIE_DESER } from "@tastyworks/tastyworks-api/session"
import Cookie from "js-cookie"
import { isNil } from "lodash-es"
import { get } from "svelte/store"
import { locale, waitLocale } from "svelte-i18n"
import { maybeInitTelemetry } from "$lib/telemetry"
import "$lib/i18n" // Import to initialize. Important
import { localStorageStore } from "$lib"
import { GlobalErrorHandler } from "$lib/result/errors/index.ts"
import { getIsMobile, getIsTastyWebView } from "$lib/util"
import * as Sentry from "@sentry/sveltekit"
import type { Alpha3Code } from "i18n-iso-countries/index"

const globalErrorHandler = new GlobalErrorHandler()

export const load = async () => {
  console.log(`account opening v${__APP_VERSION__}`)
  if (browser) {
    locale.set(window.navigator.language)
  }
  await waitLocale()

  captureUtmParams()

  setSessionTokenFromCrossDomainCookie()

  const isTastyWebView = getIsTastyWebView()

  // Resolve the transport before validate — cookie mode has no bearer token to
  // gate that call on.
  const earlyLd = browser
    ? await resolveAuthMode(get(isTastyWebView))
    : undefined
  const user = browser ? await validateSession() : null

  if (user) {
    session.user = user
    session.currentEmail = user.email
    session.externalId = user.externalId
    session.username = user.username
    lockAuthMode()
    Sentry.setUser({ email: user.email })
  } else {
    Sentry.setUser(null)
  }

  const client = await resolveLdClient(earlyLd, user)

  maybeInitTelemetry(get(client.flags.otelEnabled))

  return {
    user,
    client,
    featureFlags: client.flags,
    trackLaunchDarklyEvent: client.client.track,
    flushLD: client.client.flush,
    countryOfResidence: getCountryOfResidence(),
    isTastyWebView,
    globalErrorHandler,
  }
}

export const ssr = false
export const prerender = false

/**
 * Selects the auth transport for this load and sets it before the first
 * validate call — cookie mode has no bearer token to gate that call on.
 *
 * A session established earlier this browser session pins its transport via
 * `session.authMode` (see lockAuthMode), and a present bearer token means
 * definite bearer mode; both keep a user on the mode they started with without
 * re-reading the flag, so a mid-session flag flip can't switch a live session's
 * transport (and log a cookie user out down the token-less bearer path).
 *
 * A webview host takes bearer regardless of the flag: it hands the session
 * token to native code at completion, and in cookie mode there is no readable
 * token to hand. `auth-mode=cookie` releases that pin for hosts that read the
 * cookie themselves — it defers to the flag rather than forcing the transport.
 *
 * Only a first, token-less load outside a webview consults `web-cookie-auth`,
 * standing up LD early to read it. That load has no locked mode, and `authMode`
 * is only ever persisted alongside the identity, so there's no persisted
 * identity to target here — the early client is always anonymous, and is
 * returned so resolveLdClient can rebuild it once validate reveals a real user.
 * The other branches return undefined so LD initializes once, after validate,
 * with the resolved user.
 */
async function resolveAuthMode(
  isTastyWebView: boolean
): Promise<TastyLDClient | undefined> {
  if (session.authMode) {
    setCookieAuthEnabled(session.authMode === "cookie")
    return undefined
  }

  if (session.sessionToken) {
    setCookieAuthEnabled(false)
    return undefined
  }

  if (isTastyWebView && !isCookieModeRequested()) {
    setCookieAuthEnabled(false)
    return undefined
  }

  const client = new TastyLDClient()
  await client.initialize()
  setCookieAuthEnabled(
    getCookieAuthOverride() ?? get(client.flags.webCookieAuth)
  )

  return client
}

/**
 * Pins the resolved transport to the session on the first successful validate,
 * so every later load this browser session keeps the same auth mode regardless
 * of later `web-cookie-auth` flips. Only the first established session sets it.
 */
function lockAuthMode() {
  if (!session.authMode) {
    session.authMode = isCookieAuthEnabled() ? "cookie" : "bearer"
  }
}

/**
 * The LaunchDarkly client the app runs on, targeting the resolved user. Reuses
 * the anonymous client resolveAuthMode built on a cold cookie load unless
 * validate has since revealed a real user — in which case (and on the bearer
 * path, where no early client was built) it's rebuilt with the user context so
 * first-load flags and events target the real user rather than the next load's.
 */
async function resolveLdClient(
  early: TastyLDClient | undefined,
  user: TwUser | null
): Promise<TastyLDClient> {
  if (early && !user) {
    return early
  }

  const client = user
    ? new TastyLDClient({
        kind: "user",
        key: user.externalId,
        email: user.email,
        username: user.username,
        isEmployeeDomain: session.isEmployeeDomain,
        isNewUser: session.isNewUser,
        isMobile: getIsMobile(),
      })
    : new TastyLDClient()
  await client.initialize()
  return client
}

/**
 * Resolves the current user from the session.
 *
 * In cookie mode the HttpOnly session cookie is the source of truth, so we
 * validate unconditionally (no stored token to gate on) and let the cookie-csrf
 * transport carry the credentials. The placeholder token only satisfies the
 * service's non-empty guard. In bearer mode we keep validating the stored token.
 */
async function validateSession() {
  if (isCookieAuthEnabled()) {
    const response = await twApiClient.sessionService.validate("cookie-auth")
    return response.isError || !response.hasData() ? null : response.data
  }

  if (!session.sessionToken) {
    return null
  }

  const response = await twApiClient.sessionService.validate(
    session.sessionToken
  )
  // 401 returns null data but no error
  if (response.isError || !response.hasData()) {
    session.clear()
  }
  return response.data
}

function setSessionTokenFromCrossDomainCookie() {
  if (isCookieAuthEnabled()) {
    return
  }

  const authCookie = Cookie.get(AO_AUTH_SESSION_COOKIE_KEY)

  if (!isNil(authCookie)) {
    const payload = CROSS_DOMAIN_COOKIE_DESER.parse(authCookie)
    session.sessionToken = payload.authenticated.data.sessionToken
    Cookie.remove(AO_AUTH_SESSION_COOKIE_KEY, {
      domain: import.meta.env.VITE_SESSION_DOMAIN,
    })
  }
}

function getCountryOfResidence() {
  return localStorageStore<Alpha3Code | "">("country-of-residence", "")
}
